import _ from 'lodash';
import moment from 'moment';

import { safeToFixedDecimalScale } from './controls/ssNumericTextBox.utils';
import {
  createPrimaryKeysMap,
  getDecimalScale,
  getEntityDataPathArr,
  getEntityParentObjectKey,
  getEntityPrimaryKey,
  getModifiedDateColumn,
  toArray,
} from './form-manager.utils';
import { dateToDatabaseDateString } from '../../utils/dateUtils';
import { diff } from '../../utils/json-diff';

const NR_1 = 'NR_1';
const ATTACHMENT_DATAMEMBER = 'BAH_BASE_ATTACHMENT_GEN';
const ATTACHMENT_PRIMARY_KEY = 'BAH_ID';
const BAH_CHILD_DATAMEMBER = 'BAH_CHILD_DATAMEMBER';
const BAH_CHILD_RECORD_ID = 'BAH_CHILD_RECORD_ID';
const COLUMNS_TO_EXCLUDE = ['_tempAttachmentId', '_newRowId'];

const getFieldValue = (fields, key) => fields.find((f) => f.columnName === key)?.currentValue;

const setFieldValue = (fields, key, value) => {
  const field = fields.find((f) => f.columnName === key);
  field.currentValue = value;
};

const handleAttachmentReferences = (ssDiff) => {
  ssDiff.forEach((diff) => {
    if (diff.dataMember.includes(ATTACHMENT_DATAMEMBER)) {
      diff.rows.forEach((row) => {
        if (row.state !== 'ADDED') {
          return;
        }

        const rowId = getFieldValue(row.fields, ATTACHMENT_PRIMARY_KEY);
        // update ACO_BAH_ID to match new attachment id
        setFieldValue(row.fields, 'ACO_BAH_ID', rowId);

        const childAttachmentDatamember = getFieldValue(row.fields, BAH_CHILD_DATAMEMBER);
        const childAttachmentId = getFieldValue(row.fields, BAH_CHILD_RECORD_ID) ?? '';

        if (childAttachmentDatamember && childAttachmentId.startsWith('NR_')) {
          // look for new id using _tempKey
          let newId = childAttachmentId;
          const relatedDiff = ssDiff.find((d) => d.dataMember === childAttachmentDatamember);

          if (relatedDiff) {
            const relatedRow = relatedDiff.rows.find((r) => {
              const tempId = getFieldValue(r.fields, '_tempAttachmentId');
              return tempId === childAttachmentId;
            });

            if (relatedRow) {
              newId = getFieldValue(relatedRow.fields, 'PK_ID');
            }
          }

          // update attachment child record id to match new parent id
          setFieldValue(row.fields, BAH_CHILD_RECORD_ID, newId);
        }
      });
    }
  });

  return ssDiff;
};

const diffCleanUp = (ssDiff) => {
  ssDiff.forEach((diff) => {
    diff.rows.forEach((row) => {
      row.fields = row.fields.filter(({ columnName }) => !COLUMNS_TO_EXCLUDE.includes(columnName));
    });
  });

  return ssDiff;
};

/**
 *
 * @param dbOrig
 * @param dbModified
 * @param entityType
 * @param entityDefinition
 * @returns {Array}
 */
export function getSmartSolveDiff(dbOrig, dbModified, entityType, entityDefinition) {
  // exclude workflowSteps to do easier diff
  const workingDataOrig = { entityData: {} };
  workingDataOrig.entityData[entityType] = _.cloneDeep(dbOrig.entityData[entityType]);

  prepareDataEntityForComparison(workingDataOrig, entityDefinition, entityType); // remove temp rows
  setDataTypesByEntityDefinitionForDiffCalculation(workingDataOrig.entityData, entityDefinition);

  const workingDataModified = { entityData: {} };
  workingDataModified.entityData[entityType] = _.cloneDeep(dbModified.entityData[entityType]);

  prepareDataEntityForComparison(workingDataModified, entityDefinition, entityType);
  setDataTypesByEntityDefinitionForDiffCalculation(
    workingDataModified.entityData,
    entityDefinition
  );

  const primaryKeysMap = createPrimaryKeysMap(entityDefinition);
  const basicDiff = diff(workingDataOrig, workingDataModified, { primaryKeysMap });

  const entityPrimaryKey = getEntityPrimaryKey(entityDefinition, entityType);
  const ENTITY_PK_ID =
    entityType && dbOrig.entityData[entityType][entityPrimaryKey]
      ? dbOrig.entityData[entityType][entityPrimaryKey]
      : 'NR_1';
  const ssDiff = [];

  try {
    basic2ssDiff(
      basicDiff,
      workingDataOrig,
      workingDataModified,
      ssDiff,
      ENTITY_PK_ID,
      entityDefinition
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`getSmartSolveDiff() error ${error.stack}`);
  }

  return diffCleanUp(handleAttachmentReferences(ssDiff));
}

/**
 *
 * @param basicDiff
 * @param dbModified
 * @param ssDiff
 */
function basic2ssDiff(basicDiff, dbOriginal, dbModified, ssDiff, ENTITY_PK_ID, entityDefinition) {
  traverseDiff(basicDiff, dbOriginal, dbModified, ssDiff, '', ENTITY_PK_ID, entityDefinition);
}

/**
 *
 * @param node - For an example see the comment in the inner `else` block below.
 * @param dbModified
 * @param ssDiff - An empty row in the first execution of `traverseDiff`,
 *                 it gets mutated in inner functions.
 * @param xPath - Here are some examples:
 *                  `entityData`
 *                  `entityData.V_SS_AUDIT_PROGRAM`
 *                  `entityData.V_SS_AUDIT_PROGRAM.V_ENU_NONPERSIS_USER_DATA__added`
 *                  `entityData.V_SS_AUDIT_PROGRAM.V_SS_AUDIT_PROGRAM-V_SS_PLANNED_AUDIT`
 */
function traverseDiff(node, dbOriginal, dbModified, ssDiff, xPath, ENTITY_PK_ID, entityDefinition) {
  const props = getProps(node);

  if (typeof node === 'object' && node !== null) {
    if (Array.isArray(node)) {
      // handle arrays (row level changes)
      node.forEach((rowData, rowIndex) => {
        handleRowChanges(
          xPath,
          rowIndex,
          rowData,
          dbOriginal,
          dbModified,
          ssDiff,
          ENTITY_PK_ID,
          entityDefinition
        );
      });
    } else {
      // Handle single element.
      // An example for this is the root entity, in this case `node` will
      // be an object of this shape:
      /*
        {
          "V_SS_AUDIT_PROGRAM": {
            "V_ENU_NONPERSIS_USER_DATA__added": [{...}],
            "V_SS_AUDIT_PROGRAM-V_SS_PLANNED_AUDIT__added": [
              { ...
                "V_SS_AUDIT_PROGRAM-V_SS_PLANNED_AUDIT-V_SS_PLANNED_CRITERIA": [
                  { ...
                    "V_SS_AUDIT_PROGRAM-V_SS_PLANNED_AUDIT-V_SS_PLANNED_CRITERIA-V_SS_PLANNED_CRI_SECTION": [{...}]
                  }
                ]
              },
              {...}
            ],
            "V_SS_AUDIT_PROGRAM-V_SS_AUD_PROGRAM_TEAM": [["~", {...}]],
            "V_SS_AUDIT_PROGRAM-V_SS_AUD_PROGRAM_POLICY": [["~", {...}]]
          }
        }
      */
      Object.entries(node).forEach(([subNodeName, subNodeValue]) => {
        if (hasChildren(subNodeValue)) {
          traverseDiff(
            subNodeValue,
            dbOriginal,
            dbModified,
            ssDiff,
            xPath === '' ? subNodeName : `${xPath}.${subNodeName}`,
            ENTITY_PK_ID,
            entityDefinition
          );
        }
      });
    }
  }

  if (!_.isEmpty(props)) {
    // changes inside entity
    handleInsideRowChanges(xPath, props, dbOriginal, dbModified, ssDiff, entityDefinition);
  }
}

/**
 *
 * @param xPath
 * @param rowIndex
 * @param rowData is array: rowData[0]={" " | "~" | "+" | "-"}
 * @param dbModified
 * @param ssDiff
 */
function handleRowChanges(
  xPath,
  rowIndex,
  rowData,
  dbOriginal,
  dbModified,
  ssDiff,
  ENTITY_PK_ID,
  entityDefinition
) {
  let dataMember;
  let columnName;
  let rowid;
  let modifiedDate = null;
  let originalValue = null;
  let currentValue = null;

  if (xPath.indexOf('.') > -1) {
    const xPathArr = xPath.split('.');
    dataMember = xPathArr[xPathArr.length - 1];
  } else {
    dataMember = xPath;
  }

  const entityPath = `${xPath}[${rowIndex}]`;

  if (Array.isArray(rowData)) {
    switch (rowData[0]) {
      case '':
        // DO NOTHING
        break;

      case '~':
        rowid = getRowId(entityPath, dbOriginal, dataMember, entityDefinition);
        modifiedDate = getModifiedDate(entityPath, dbOriginal, dataMember, entityDefinition);

        Object.entries(rowData[1]).forEach(([colName, newValue]) => {
          columnName = colName;

          if (columnName.split('-').length > 1 && _.isArray(newValue)) {
            // nested sub entity (e.g. V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC__added)
            const subEntityArr = newValue;
            const subDataMember = columnName.split('__')[0]; // V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC__added
            const subDiffState = columnName.split('__')[1] === 'added' ? 'ADDED' : 'UNKNOWN';
            const parentObjectKeyId = rowid;

            handleChangesForSubEntity(
              entityPath,
              subEntityArr,
              subDataMember,
              ssDiff,
              subDiffState,
              entityDefinition,
              parentObjectKeyId,
              dbOriginal,
              dbModified
            );
          } else {
            originalValue = newValue?.__old;
            currentValue = newValue?.__new;

            // modified
            createSmartSolveDiffEntry(
              dataMember,
              rowid,
              modifiedDate,
              columnName,
              originalValue,
              currentValue,
              'MODIFIED',
              ssDiff,
              entityDefinition
            );
          }
        });
        break;

      case '-':
        // resolve deleted rowid from rowData (from basic diff)
        rowid = _.get(rowData[1], getEntityPrimaryKey(entityDefinition, dataMember));

        // deleted
        createSmartSolveDiffEntry(
          dataMember,
          rowid,
          modifiedDate,
          columnName,
          originalValue,
          currentValue,
          'DELETED',
          ssDiff,
          entityDefinition
        );
        break;

      case '+':
        rowid = generateRowId(dataMember, ssDiff);
        modifiedDate = null;

        setRowIds(entityDefinition, dataMember, rowData[1], rowid, ENTITY_PK_ID);

        Object.entries(rowData[1]).forEach(([colName, newValue]) => {
          columnName = colName;

          if (columnName.split('-').length > 1 && _.isArray(newValue)) {
            // nested sub entity (e.g. V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC__added)
            const subEntityArr = newValue;
            const subDataMember = columnName; // V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC
            const subDiffState = 'ADDED'; // inside '+' ADDED only
            const parentObjectKeyId = rowid;

            handleChangesForSubEntity(
              entityPath,
              subEntityArr,
              subDataMember,
              ssDiff,
              subDiffState,
              entityDefinition,
              parentObjectKeyId,
              dbOriginal,
              dbModified
            );
          } else {
            // added
            originalValue = null;
            currentValue = newValue;

            createSmartSolveDiffEntry(
              dataMember,
              rowid,
              modifiedDate,
              columnName,
              originalValue,
              currentValue,
              'ADDED',
              ssDiff,
              entityDefinition
            );
          }
        });
        break;

      default:
        break;
    }
  } else {
    // same as '+' in case above

    dataMember = dataMember.substring(0, dataMember.indexOf('__added'));
    rowid = generateRowId(dataMember, ssDiff);
    modifiedDate = null;

    setRowIds(entityDefinition, dataMember, rowData, rowid, ENTITY_PK_ID);

    Object.entries(rowData).forEach(([colName, newValue]) => {
      columnName = colName;
      originalValue = null;
      currentValue = newValue;

      if (columnName.split('-').length > 1 && _.isArray(newValue)) {
        // nested sub entity (e.g. V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC)
        const subDiffState = 'ADDED';
        const subEntityArr = newValue;
        const subDataMember = columnName;
        const parentObjectKeyId = rowid;

        handleChangesForSubEntity(
          entityPath,
          subEntityArr,
          subDataMember,
          ssDiff,
          subDiffState,
          entityDefinition,
          parentObjectKeyId,
          dbOriginal,
          dbModified
        );
      } else {
        // added
        createSmartSolveDiffEntry(
          dataMember,
          rowid,
          modifiedDate,
          columnName,
          originalValue,
          currentValue,
          'ADDED',
          ssDiff,
          entityDefinition
        );
      }
    });
  }
}

/**
 *
 * @param xPath
 * @param subEntityArr
 * @param subDataMember
 * @param ssDiff
 * @param subDiffState
 * @param entityDefinition
 * @param rowid - parentObjectKey
 * @param dbModified
 */
function handleChangesForSubEntity(
  xPath,
  subEntityArr,
  subDataMember,
  ssDiff,
  subDiffState,
  entityDefinition,
  parentObjectKeyId,
  dbOriginal,
  dbModified
) {
  let subModifiedDate = null;

  subEntityArr.forEach((subEntity, subEntityRowIndex) => {
    let subRowid = generateRowId(subDataMember, ssDiff);
    const subEntityPath = `${xPath}.${subDataMember}[${subEntityRowIndex}]`;

    if (Array.isArray(subEntity)) {
      switch (subEntity[0]) {
        case '':
          // DO NOTHING
          break;

        case '~':
          subRowid = getRowId(subEntityPath, dbOriginal, subDataMember, entityDefinition);
          subModifiedDate = getModifiedDate(
            subEntityPath,
            dbOriginal,
            subDataMember,
            entityDefinition
          );

          Object.entries(subEntity[1]).forEach(([colName, newValue]) => {
            const subColumnName = colName;
            const subOriginalValue = newValue.__old;
            const subCurrentValue = newValue.__new;

            // If there are sub entities, process them...
            if (colName.split('-').length > 1 && _.isArray(newValue)) {
              const dataMember = colName.split('__')[0];
              const diffState = colName.split('__')[1] === 'added' ? 'ADDED' : 'UNKNOWN';

              handleChangesForSubEntity(
                subEntityPath,
                newValue,
                dataMember,
                ssDiff,
                diffState,
                entityDefinition,
                subRowid,
                dbOriginal,
                dbModified
              );
            } else {
              // ...if not, create SmartSolve diff entry.
              createSmartSolveDiffEntry(
                subDataMember,
                subRowid,
                subModifiedDate,
                subColumnName,
                subOriginalValue,
                subCurrentValue,
                'MODIFIED',
                ssDiff,
                entityDefinition
              );
            }
          });
          break;

        case '-':
          // resolve deleted subRowid from subEntity (from basic diff)
          subRowid = _.get(subEntity[1], getEntityPrimaryKey(entityDefinition, subDataMember));

          // deleted
          createSmartSolveDiffEntry(
            subDataMember,
            subRowid,
            subModifiedDate,
            null,
            null,
            null,
            'DELETED',
            ssDiff,
            entityDefinition
          );
          break;

        case '+':
          subRowid = generateRowId(subDataMember, ssDiff);
          setRowIds(entityDefinition, subDataMember, subEntity[1], subRowid, parentObjectKeyId);
          subModifiedDate = null;

          // The `subEntity` variable is the diff array: ['+' or '-' or '~', { the data }]
          // That's why we iterate the `subEntity[1]` object entries.
          Object.entries(subEntity[1]).forEach(([colName, newValue]) => {
            if (colName.split('-').length > 1 && _.isArray(newValue)) {
              const dataMember = colName.split('__')[0];
              const diffState = colName.split('__')[1] === 'added' ? 'ADDED' : 'UNKNOWN';

              // Handle new subentities inside new rows for modified entities.
              // If array is missing the change type string (+, - or ~)
              // and the data is not in the "dbOriginal" then the data is new (added)
              const modified = _.get(dbOriginal, `${subEntityPath}.${dataMember}`);

              if (!['+', '-', '~'].includes(newValue[0]) && !modified) {
                newValue.forEach((rowData, rowIndex) => {
                  handleRowChanges(
                    `${xPath}.${dataMember}__added`,
                    rowIndex,
                    rowData,
                    dbOriginal,
                    dbModified,
                    ssDiff,
                    subRowid,
                    entityDefinition
                  );
                });
              } else {
                handleChangesForSubEntity(
                  subEntityPath,
                  newValue,
                  dataMember,
                  ssDiff,
                  diffState,
                  entityDefinition,
                  subRowid,
                  dbOriginal,
                  dbModified
                );
              }
            } else {
              // added
              createSmartSolveDiffEntry(
                subDataMember,
                subRowid,
                subModifiedDate,
                colName,
                null, // originalValue
                newValue,
                'ADDED',
                ssDiff,
                entityDefinition
              );
            }
          });
          break;

        default:
          break;
      }
    } else {
      // If `subEntity` is not an array then it's object to add.
      // (Arrays are for diff objects: ['+' or '-' or '~', { the data }], see the 'switch' above)
      setRowIds(entityDefinition, subDataMember, subEntity, subRowid, parentObjectKeyId);

      Object.entries(subEntity).forEach(([subColName, newValue]) => {
        if (subColName.split('-').length > 1 && _.isArray(newValue)) {
          handleChangesForSubEntity(
            subEntityPath,
            newValue,
            subColName,
            ssDiff,
            'ADDED',
            entityDefinition,
            subRowid,
            dbOriginal,
            dbModified
          );
        } else {
          createSmartSolveDiffEntry(
            subDataMember,
            subRowid,
            subModifiedDate,
            subColName,
            null, // originalValue
            newValue,
            subDiffState,
            ssDiff,
            entityDefinition
          );
        }
      });
    }
  });
}

const setRowIds = (entityDefinition, dataMember, rowData, rowid, ENTITY_PK_ID) => {
  const primaryKey = getEntityPrimaryKey(entityDefinition, dataMember);
  const parentObjectKey = getEntityParentObjectKey(entityDefinition, dataMember);
  rowData[primaryKey] = rowid;
  rowData[parentObjectKey] = ENTITY_PK_ID;
};

/**
 * Handle inside row changes - MODIFY fields inside row
 *
 * @param xPath
 * @param props
 * @param dbModified
 * @param ssDiff
 */
function handleInsideRowChanges(xPath, props, dbOriginal, dbModified, ssDiff, entityDefinition) {
  const xPathArr = xPath.split('.');
  let dataMember = null;
  let columnName = null;
  // const dataMemberXPath = xPath.slice(0, xPath.length - columnName.length - 1);
  let rowid = null;
  let modifiedDate = null;
  let originalValue = null;
  let currentValue = null;
  let diffState = null;

  const modifiedEntiries = {};

  Object.entries(props).forEach(([key, value]) => {
    // xPath data.entityDataStore.entityData.V_SS_CAPA props {"OWNING_SITE_CODE__deleted":"DEFAULT","BRAND_NEW__added":"BRAND_NEW_DATA"}

    if (key.endsWith('__added')) {
      dataMember = xPathArr[xPathArr.length - 1];
      columnName = key.slice(0, key.length - '__added'.length);
      rowid = getRowId(xPath, dbOriginal, dataMember, entityDefinition);
      diffState = 'MODIFIED';

      if (rowid === undefined) {
        rowid = NR_1;
        diffState = 'ADDED';
      }

      modifiedDate = getModifiedDate(xPath, dbModified, dataMember, entityDefinition);
      originalValue = null;
      currentValue = value;

      // if new entry is created PK_ID is must required, so create PK_ID
      if (diffState === 'ADDED') {
        const primaryKeyColumnName = getEntityPrimaryKey(entityDefinition, dataMember);

        createSmartSolveDiffEntry(
          dataMember,
          rowid,
          modifiedDate,
          primaryKeyColumnName,
          originalValue,
          NR_1,
          diffState,
          ssDiff,
          entityDefinition
        );
      }
      createSmartSolveDiffEntry(
        dataMember,
        rowid,
        modifiedDate,
        columnName,
        originalValue,
        currentValue,
        diffState,
        ssDiff,
        entityDefinition
      );
    } else if (key.endsWith('__deleted')) {
      dataMember = xPathArr[xPathArr.length - 1];
      columnName = key.slice(0, key.length - '__deleted'.length);
      rowid = getRowId(xPath, dbOriginal, dataMember, entityDefinition);
      modifiedDate = getModifiedDate(xPath, dbOriginal, dataMember, entityDefinition);
      originalValue = value;
      currentValue = null;

      createSmartSolveDiffEntry(
        dataMember,
        rowid,
        modifiedDate,
        columnName,
        originalValue,
        currentValue,
        'MODIFIED',
        ssDiff,
        entityDefinition
      );
    } else {
      modifiedEntiries[key] = value;
    }
  });

  if (!_.isEmpty(modifiedEntiries)) {
    // xPath data.entityDataStore.entityData.V_SS_CAPA.PRODUCT_CODE

    dataMember = xPathArr[xPathArr.length - 2];
    columnName = xPathArr[xPathArr.length - 1];
    const dataMemberXPath = xPath.slice(0, xPath.length - columnName.length - 1);
    rowid = getRowId(dataMemberXPath, dbOriginal, dataMember, entityDefinition);
    modifiedDate = getModifiedDate(dataMemberXPath, dbOriginal, dataMember, entityDefinition);
    originalValue = modifiedEntiries.__old;
    currentValue = modifiedEntiries.__new;

    // modified
    createSmartSolveDiffEntry(
      dataMember,
      rowid,
      modifiedDate,
      columnName,
      originalValue,
      currentValue,
      'MODIFIED',
      ssDiff,
      entityDefinition
    );
  }
}

/**
 *
 * @param dataMember
 * @param rowid
 * @param modifiedDate
 * @param columnName
 * @param originalValue
 * @param currentValue
 * @param diffState
 * @param ssDiff
 */
function createSmartSolveDiffEntry(
  dataMember,
  rowid,
  modifiedDate,
  columnName,
  originalValue,
  currentValue,
  diffState,
  ssDiff,
  entityDefinition
) {
  let dataMemberDiff = _.find(ssDiff, { dataMember });

  if (dataMemberDiff === undefined) {
    dataMemberDiff = {
      dataMember,
      rows: [],
    };
    ssDiff.push(dataMemberDiff);
  }

  let row = _.find(dataMemberDiff.rows, { rowid, state: diffState });

  if (row === undefined) {
    row = {
      state: diffState,
      rowid,
      fields: [],
      modifiedDateColumnName: getModifiedDateColumn(entityDefinition, dataMember),
      modifiedDate,
    };

    dataMemberDiff.rows.push(row);
  }

  // columnName can be null in case of deletion
  if (columnName) {
    const column = _.find(row.fields, { columnName });

    if (column === undefined) {
      row.fields.push({
        columnName,
        originalValue,
        currentValue,
      });
    }
  }
}

/**
 * remove rows from data model with row.uiTemporaryRow === true
 * @param entity
 */
function removeTempRows(entity) {
  Object.entries(entity).forEach(([propName, propValue]) => {
    if (Array.isArray(propValue)) {
      // filter out rows with uiTemporaryRow === true
      entity[propName] = propValue.filter((obj) => !obj.uiTemporaryRow);
      entity[propName].forEach((subEntity) => removeTempRows(subEntity));
    }
  });
}

/**
 *
 * @param dbModified
 * @param entityDefinition
 * @param entityType
 */
function prepareDataEntityForComparison(dbModified, entityDefinition, entityType) {
  const topEntity = _.get(dbModified, `entityData.${entityType}`);

  if (topEntity.V_ENU_NONPERSIS_USER_DATA) {
    topEntity.V_ENU_NONPERSIS_USER_DATA = toArray(topEntity.V_ENU_NONPERSIS_USER_DATA);
  }

  removeTempRows(topEntity);
  newRowsGoLast(topEntity, entityDefinition, entityType);
}

/**
 * Perform recursive loop and move new rows (IsNewObject === true) in sub-entities at the end of array.
 *
 * If new rows go first (as we do in addRow()) and existing rows have very similar data it leads to issue:
 * 'json-diff' think that new row is modified (with deleted PK_ID, etc) and existing row is added (EUROPA-1830).
 * So, when client changes are produced (basic diff step) - new rows should go last
 *
 * @param entity
 * @param entityDefinition
 * @param dataMemberPrefix
 */
function newRowsGoLast(entity, entityDefinition, dataMemberPrefix) {
  Object.entries(entity).forEach(([propName, propValue]) => {
    // check if sub-entity (e.g. V_SS_CAPA-V_SS_CAPA_RISK_ASSESSMENT)
    // if prop has prop RelationShipKey in entity definition -> it's sub-entity -> sort array
    // e.g. V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_RISK_ASSESSMENT.properties.RelationShipKey
    if (_.get(entityDefinition, `${dataMemberPrefix}.${propName}.properties.RelationShipKey`)) {
      // propValue should be always array
      // when it isn't -> bug in rules (find how that value was set)
      if (Array.isArray(propValue)) {
        const existingObjectsArr = propValue.filter((obj) => obj.IsNewObject !== true);
        const newObjectsArr = propValue.filter((obj) => obj.IsNewObject === true);
        // clean up 'ContainerInstanceIdForHandMadeNewObject' and '_tempAttachmentId' prop created/used for frontend only
        newObjectsArr.forEach((obj) => {
          delete obj.ContainerInstanceIdForHandMadeNewObject;
          // delete obj._tempAttachmentId;
        });
        entity[propName] = [].concat(existingObjectsArr).concat(newObjectsArr);
        entity[propName].forEach((subEntity) =>
          newRowsGoLast(subEntity, entityDefinition, `${dataMemberPrefix}.${propName}`)
        );
      } else {
        // eslint-disable-next-line no-console
        console.log(
          `propValue for ${dataMemberPrefix}.${propName} should be array - fix the bug in rules`
        );
      }
    }
  });
}

/**
 *
 * @param xPath
 * @param dataModel
 */
function getRowId(xPath, dataModel, dataMember, entityDefinition) {
  const primaryKey = getEntityPrimaryKey(entityDefinition, dataMember);
  return _.get(dataModel, `${xPath}.${primaryKey}`);
}

/**
 *
 * @param xPath
 * @param dbModified
 */
function getModifiedDate(xPath, dbModified, dataMember, entityDefinition) {
  const modifiedDateColumn = getModifiedDateColumn(entityDefinition, dataMember);
  return _.get(dbModified, `${xPath}.${modifiedDateColumn}`);
}

function getProps(node) {
  const props = {};

  if (typeof node === 'object' && node !== null) {
    Object.entries(node).forEach(([key, value]) => {
      if (!hasChildren(value)) {
        props[key] = value;
      } else if (moment.isDate(value)) {
        props[key] = dateToDatabaseDateString(value);
      }
    });
  }

  return props;
}

function hasChildren(node) {
  return !!(typeof node === 'object' && node !== null);
}

function generateRowId(dataMember, ssDiff) {
  const dataMemberDiff = _.find(ssDiff, { dataMember });

  if (dataMemberDiff !== undefined && Array.isArray(dataMemberDiff.rows)) {
    const filteredRows = dataMemberDiff.rows.filter((row) => row.state === 'ADDED');

    if (filteredRows.length === 0) {
      return NR_1;
    }

    const lastRowId = filteredRows[filteredRows.length - 1].rowid;

    if (lastRowId !== undefined) {
      const index = lastRowId.indexOf('_');
      const counter = parseInt(lastRowId.substring(index + 1, lastRowId.length), 10);
      const newRowId = `NR_${counter + 1}`;
      return newRowId;
    }
  }

  return NR_1;
}

/**
 * convert object to string based representation consumable by json-diff
 */
function setDataTypesByEntityDefinitionForDiffCalculation(data, entityDefinition) {
  // handle fields
  function updateFieldForDiffCalculation(data, columnName, columnProperties, entityDataPathArr) {
    entityDataPathArr.forEach((calculatedEntityDataPath) => {
      const fieldDataPath = `${calculatedEntityDataPath}.${columnName}`;
      const fieldData = _.get(data, fieldDataPath);

      // no data -> nothing to fix
      if (_.isNil(fieldData) || fieldData === '') {
        return;
      }

      switch (columnProperties.DataType) {
        case 'System.Byte':
        case 'System.Boolean':
        case 'System.Int32':
          break;

        case 'System.Decimal': {
          const decimalScale = getDecimalScale(
            columnProperties.DataType,
            columnProperties.FormatType,
            fieldData
          );
          _.set(data, fieldDataPath, +safeToFixedDecimalScale(decimalScale, fieldData));
          break;
        }

        case 'System.DateTime':
          // date obj to str (2020-01-02T02:00:00.000)
          _.set(data, fieldDataPath, dateToDatabaseDateString(fieldData));
          break;

        default:
        // do nothing
      }
    });
  }

  // handle entities
  function updateEntityForDiffCalculation(data, entityDefinitionNode) {
    const { columns } = entityDefinitionNode;
    const entityProperties = entityDefinitionNode.properties;

    Object.entries(columns).forEach(([columnName, params]) => {
      const entityDataPathArr = getEntityDataPathArr(entityProperties.EntityDataPath, data);

      // check if data exist for 'entityProperties.EntityDataPath' in data model
      if (entityDataPathArr.length > 0) {
        updateFieldForDiffCalculation(data, columnName, params.properties, entityDataPathArr);
      }
    });

    // validate child entities
    Object.entries(entityDefinitionNode).forEach(([key, node]) => {
      if (!['columns', 'properties'].includes(key)) {
        updateEntityForDiffCalculation(data, node); // child entities
      }
    });
  }

  // eslint-disable-next-line no-unused-vars
  Object.entries(entityDefinition).forEach(([key, entityDefinitionNode]) => {
    updateEntityForDiffCalculation(data, entityDefinitionNode);
  });
}
